import { useState, useMemo, useCallback, memo } from "react";
import type { BiddingInfoItem, BiddingInfoSubItem } from "../../../types";
import { ListingCategoryConst } from "../../../constants";
import Big from "big.js";
//----------------
// Props
//----------------
interface BiddingItemMaintenanceSplitFormProps {
  originalItem: BiddingInfoItem;
  onConfirm: (newSubItems: BiddingInfoSubItem[]) => void;
  onCancel: () => void;
}

//----------------
// 出品管理の分割フォームコンポーネント
//----------------
export const BiddingItemMaintenanceSplitForm = memo(
  ({
    originalItem,
    onConfirm,
    onCancel,
  }: BiddingItemMaintenanceSplitFormProps) => {
    //----------------
    // Satet定義
    //----------------

    // 出品サブアイテム一覧(分割明細一覧)
    const [splitRows, setSplitRows] = useState<BiddingInfoSubItem[]>(
      // splitRowsの初期値の設定
      originalItem.subItems && originalItem.subItems.length > 0
        ? // 元データが分割されている場合：元データの分割明細をコピー
          [...originalItem.subItems]
        : // 元データが分割されていない場合：枝番1で1明細分作成
          [
            {
              capacity1: "",
              quantity1: "",
              capacity2: "",
              quantity2: "",
              listingCategory: ListingCategoryConst.BIDDING,
              branchNumber: 1, // 最初の枝番を 1 に設定 ('' ではない)
              approvalStatus: 0,
              biddingNumber: 0
            },
          ],
    );

    //----------------
    // Memo定義
    //----------------

    // // 総量計算
    // const { totalCapacity } = useMemo(() => {
    //   return {
    //     totalCapacity:
    //       (Number(originalItem.capacity1) || 0) *
    //         (Number(originalItem.quantity1) || 0) +
    //       (Number(originalItem.capacity2) || 0) *
    //         (Number(originalItem.quantity2) || 0),
    //   };
    // }, [originalItem]);

    // // 残容量計算
    // const { remainingCapacity } = useMemo(() => {
    //   const currentTotal = splitRows.reduce(
    //     // 第1引数：コールバック関数
    //     (sum, row) =>
    //       sum +
    //       ((parseFloat(String(row.capacity1)) || 0) *
    //         (parseInt(String(row.quantity1), 10) || 0) +
    //         (parseFloat(String(row.capacity2)) || 0) *
    //           (parseInt(String(row.quantity2), 10) || 0)),
    //     // 第2引数：初期値
    //     0,
    //   );
    //   return { remainingCapacity: totalCapacity - currentTotal };
    // }, [splitRows, totalCapacity]);

    // ① 総量計算
    const { totalCapacity } = useMemo(() => {
      try {
        const c1 = new Big(originalItem.capacity1 || 0);
        const q1 = new Big(originalItem.quantity1 || 0);
        const c2 = new Big(originalItem.capacity2 || 0);
        const q2 = new Big(originalItem.quantity2 || 0);

        // (c1 * q1) + (c2 * q2)
        const total = c1.times(q1).plus(c2.times(q2));

        return { totalCapacity: total.toNumber() };
      } catch (e) {
        return { totalCapacity: 0 };
      }
    }, [originalItem]);

    // ② 残容量計算
    const { remainingCapacity } = useMemo(() => {
      try {
        // splitRows の合計を 10進数で計算
        const currentTotalBig = splitRows.reduce((sum, row) => {
          const rowC1 = new Big(row.capacity1 || 0);
          const rowQ1 = new Big(row.quantity1 || 0);
          const rowC2 = new Big(row.capacity2 || 0);
          const rowQ2 = new Big(row.quantity2 || 0);

          // 行の合計 = (c1 * q1) + (c2 * q2)
          const rowSum = rowC1.times(rowQ1).plus(rowC2.times(rowQ2));

          // 全体の合計に加算
          return sum.plus(rowSum);
        }, new Big(0)); // 初期値も Big オブジェクトにするのがコツ

        // 残容量 = 総量 - 現在の合計
        const totalBig = new Big(totalCapacity);
        const remaining = totalBig.minus(currentTotalBig);

        return { remainingCapacity: remaining.toNumber() };
      } catch (e) {
        return { remainingCapacity: 0 };
      }
    }, [splitRows, totalCapacity]);

    //----------------
    // イベントハンドラー定義
    //----------------

    // 出品サブアイテム(分割明細)の入力内容変更関数
    // 出品サブアイテム(分割明細)の入力内容変更時、出品サブアイテム一覧(分割明細一覧)stateに反映させる
    const handleRowChange = useCallback(
      (
        branchNumber: number | string,
        field: keyof BiddingInfoSubItem,
        value: string | number,
      ) => {
        setSplitRows(
          // 1.現状値(rows)を取り出して、
          (rows) =>
            // 2.１要素(r)ずつidが引数.idと等しいかどうかをチェックし、
            rows.map(
              (r) =>
                r.branchNumber === branchNumber
                  ? // 2-a).等しい場合：引数で指定されたカラム(field)の値を引数で指定された内容(value)で置き換えたバージョンの新しい要素を作成し返す
                    { ...r, [field]: value }
                  : // 2-b):等しくない場合：要素をそのまま返す
                    r,
              // 3.処理した結果の新しい配列を返す
            ),
          // 4.splitRowsの内容を処理した結果で更新する
        );
      },
      [setSplitRows],
    );

    // 出品サブアイテム一覧(分割明細一覧)新規行追加関数
    // 出品サブアイテム(分割明細)の追加ボタン押下時、出品サブアイテム一覧(分割明細一覧)stateに新規行を追加する
    const addRow = useCallback(() => {
      setSplitRows((rows) => {
        // 'rows' は現在の splitRows の state
        // 1. 現在の rows 配列から最大の branchNumber を見つける
        // (rows が空の場合は 0 を初期値とする)
        const maxBranchNumber = rows.reduce((max, row) => {
          const currentNum = Number(row.branchNumber) || 0; // 念のため Number() で数値化
          return currentNum > max ? currentNum : max;
        }, 0);

        // 2. 新しい行の branchNumber は max + 1
        const nextBranchNumber = maxBranchNumber + 1;

        // 3. 新しい行オブジェクトを作成
        //    (BiddingInfoSubItem の型定義に合わせる)
        const newRow: BiddingInfoSubItem = {
          branchNumber: nextBranchNumber,
          capacity1: "",
          quantity1: "",
          capacity2: "",
          quantity2: "",
          listingCategory: ListingCategoryConst.BIDDING,
          approvalStatus: 0,
          biddingNumber: 0
          // ItemId や BiddingKb など、BiddingInfoSubItem 型に必要な
          // 他のプロパティがあれば、ここでデフォルト値を追加してください
        };

        // 4. 既存の行リストに新しい行を追加して返す
        return [...rows, newRow];
      });
    }, [setSplitRows]);

    // 出品サブアイテム一覧(分割明細一覧)行削除関数
    // 出品サブアイテム(分割明細)の削除ボタン押下時、出品サブアイテム一覧(分割明細一覧)stateから該当行を削除する
    const removeRow = useCallback(
      (branchNumber: number | string) => {
        // splitRowsから引数.idに一致しない状態の配列を新たに作成して、splitRowsを更新する
        setSplitRows((rows) =>
          rows.filter((r) => r.branchNumber !== branchNumber),
        );
      },
      [setSplitRows],
    );

    // 出品サブアイテム一覧(分割明細一覧)編集確定関数
    const handleConfirmClick = useCallback(() => {
      // 1. フィルター処理：
      //    少なくとも「①のペア」または「②のペア」が
      //    両方入力されている行だけを選ぶ。
      const processedRows = splitRows
        .filter(
          (row) =>
            (row.capacity1 && row.quantity1) ||
            (row.capacity2 && row.quantity2),
        )
        // 2. マッピング処理：
        //    フィルターを通過した行に対して、
        //    空文字（''）を 0 に変換する。
        .map((row) => {
          // 既存のプロパティ (branchNumber, listingCategoryなど) を保持
          return {
            ...row,
            // Number('') は 0 を返すので、|| 0 も不要かもしれませんが、
            // 可読性と安全性のために Number() || 0 を使います。
            capacity1: Number(row.capacity1) || 0,
            quantity1: Number(row.quantity1) || 0,
            capacity2: Number(row.capacity2) || 0,
            quantity2: Number(row.quantity2) || 0,
          };
        });

      // 3. 整形済みのデータをonConfirmに渡す
      onConfirm(processedRows);
    }, [splitRows, onConfirm]);

    //----------------
    // その他定義
    //----------------

    // 編集確定ボタン制御
    // 分割明細あり＆残容量ありの場合は確定できないように制御
    const isConfirmDisabled = remainingCapacity !== 0 && splitRows.length > 0;

    // 入力項目の共通スタイル
    const splitInputClasses =
      "w-full p-2 border border-slate-300 rounded-md bg-yellow-50 focus:ring-yellow-500 focus:border-yellow-500";

    //----------------
    // レンダリング処理
    //----------------
    return (
      <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border border-slate-200">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
            <p className="text-sm font-medium text-slate-600">総量</p>
            <p className="text-2xl font-bold text-slate-900">
              {totalCapacity} kg
            </p>
          </div>
          <div
            className={`p-4 rounded-lg border ${remainingCapacity === 0 ? "border-green-300 bg-green-50" : "border-orange-300 bg-orange-50"}`}
          >
            <p className="text-sm font-medium text-slate-600">残り</p>
            <p
              className={`text-2xl font-bold ${remainingCapacity === 0 ? "text-green-700" : "text-orange-700"}`}
            >
              {remainingCapacity} kg
            </p>
            {remainingCapacity !== 0 && (
              <p className="text-xs text-orange-600 mt-1">
                総量と一致させてください
              </p>
            )}
          </div>
        </div>

        <div className="space-y-4 mb-6">
          <label className="text-base font-semibold text-slate-900">
            分割内訳
          </label>
          <div className="hidden sm:grid grid-cols-12 px-3 py-2 text-xs font-medium text-slate-500 border-b border-slate-200">
            <div className="col-span-3 pr-4">① 容量 × 本数</div>
            <div className="col-span-3 px-4 border-l border-r border-slate-200">
              ② 容量 × 本数
            </div>
            <div className="col-span-2 pl-4">小計</div>
            <div className="col-span-3 pl-4 border-l border-r border-slate-200">
              出品区分
            </div>
            <div className="col-span-1 text-right">削除</div>
          </div>

          {splitRows.map((row) => (
            <div
              key={row.branchNumber}
              className="p-3 bg-slate-50 rounded-lg border border-slate-200 grid grid-cols-12 gap-y-3 items-center"
            >
              {/* ① 容量/本数 (スマホ: 12列, PC: 4列) */}
              <div className="col-span-12 sm:col-span-3 flex items-center gap-2 sm:pr-4">
                <label className="text-sm font-medium text-slate-700 sm:hidden">
                  ①
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="容量(kg)"
                  value={row.capacity1}
                  onChange={(e) =>
                    handleRowChange(
                      row.branchNumber,
                      "capacity1",
                      e.target.value,
                    )
                  }
                  className={splitInputClasses}
                />
                <span className="text-slate-500">×</span>
                <input
                  type="number"
                  placeholder="本数"
                  value={row.quantity1}
                  onChange={(e) =>
                    handleRowChange(
                      row.branchNumber,
                      "quantity1",
                      e.target.value,
                    )
                  }
                  className={splitInputClasses}
                />
              </div>

              {/* ② 容量/本数 (スマホ: 12列, PC: 4列) */}
              <div className="col-span-12 sm:col-span-3 flex items-center gap-2 sm:px-4 sm:border-l sm:border-r sm:border-slate-300">
                <label className="text-sm font-medium text-slate-700 sm:hidden">
                  ②
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="容量(kg)"
                  value={row.capacity2}
                  onChange={(e) =>
                    handleRowChange(
                      row.branchNumber,
                      "capacity2",
                      e.target.value,
                    )
                  }
                  className={splitInputClasses}
                />
                <span className="text-slate-500">×</span>
                <input
                  type="number"
                  placeholder="本数"
                  value={row.quantity2}
                  onChange={(e) =>
                    handleRowChange(
                      row.branchNumber,
                      "quantity2",
                      e.target.value,
                    )
                  }
                  className={splitInputClasses}
                />
              </div>
              <div className="col-span-12 sm:col-span-2 flex items-center gap-2 sm:px-4 ">
                <div>
                  <p className="font-semibold">
                    {Number(row.quantity1 || 0) * Number(row.capacity1 || 0) +
                      Number(row.quantity2 || 0) *
                        Number(row.capacity2 || 0)}
                    kg
                  </p>
                </div>
              </div>

              {/* 出品区分 (スマホ: 10列, PC: 3列) */}
              <div className="col-span-10 sm:col-span-3 flex items-center gap-4 sm:gap-2 md:gap-4 sm:pl-4 sm:px-4 sm:border-l sm:border-r">
                <label className="flex items-center text-sm">
                  <input
                    type="radio"
                    name={`listingCategory-${row.branchNumber}`}
                    value={ListingCategoryConst.PRIVATE}
                    checked={
                      row.listingCategory === ListingCategoryConst.PRIVATE
                    }
                    onChange={(e) =>
                      handleRowChange(
                        row.branchNumber,
                        "listingCategory",
                        Number(e.target.value),
                      )
                    }
                    className="h-4 w-4"
                  />
                  <span className="ml-1.5">相対</span>
                </label>
                <label className="flex items-center text-sm">
                  <input
                    type="radio"
                    name={`listingCategory-${row.branchNumber}`}
                    value={ListingCategoryConst.BIDDING}
                    checked={
                      row.listingCategory === ListingCategoryConst.BIDDING
                    }
                    onChange={(e) =>
                      handleRowChange(
                        row.branchNumber,
                        "listingCategory",
                        Number(e.target.value),
                      )
                    }
                    className="h-4 w-4"
                  />
                  <span className="ml-1.5">通常入札</span>
                </label>
                <label className="flex items-center text-sm">
                  <input
                    type="radio"
                    name={`listingCategory-${row.branchNumber}`}
                    value={ListingCategoryConst.SALE}
                    checked={row.listingCategory === ListingCategoryConst.SALE}
                    onChange={(e) =>
                      handleRowChange(
                        row.branchNumber,
                        "listingCategory",
                        Number(e.target.value),
                      )
                    }
                    className="h-4 w-4"
                  />
                  <span className="ml-1.5">入札会</span>
                </label>
              </div>

              {/* 削除ボタン (スマホ: 2列, PC: 1列) */}
              <div className="col-span-2 sm:col-span-1 flex justify-end items-center">
                <button
                  onClick={() => removeRow(row.branchNumber)}
                  className="p-2 rounded-full text-slate-400 hover:bg-red-100 hover:text-red-600 disabled:opacity-50"
                  title="この行を削除"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-between items-center mt-6">
          <button
            onClick={addRow}
            className="px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-md hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            ＋ 分割を追加
          </button>
          <div className="flex gap-4">
            <button
              onClick={onCancel}
              className="px-6 py-2 rounded-md bg-slate-200 hover:bg-slate-300"
            >
              キャンセル
            </button>
            <button
              onClick={handleConfirmClick}
              disabled={isConfirmDisabled}
              className="px-6 py-2 rounded-md bg-indigo-600 text-white disabled:bg-slate-300"
            >
              確定
            </button>
          </div>
        </div>
      </div>
    );
  },
);
